import React, { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Shield,
  FileText,
  Building,
  Loader2,
  CreditCard
} from 'lucide-react';
import { loadHyper } from '@juspay-tech/hyper-js';
import { HyperElements, UnifiedCheckout } from '@juspay-tech/react-hyper-js';
import { getCurrentTenantConfig } from '../config/tenant';
import { formatDate } from '../utils/dateUtils';
import {
  parseRedirectError,
  parseSDKError,
  formatErrorForDisplay,
  logPaymentError,
  type HyperswitchError
} from '../lib/hyperswitchErrors';

// Types
interface InvoicePaymentData {
  invoiceNumber: string;
  amountDue: number;
  totalAmount: number;
  subtotal?: number;
  shippingAmount?: number;
  telemedNetworkFee?: number;
  taxAmount?: number;
  amountPaid: number;
  dueDate: string;
  status: string;
  physicianName: string;
  clinicName: string;
  companyName: string;
  hasHyperswitchCustomer: boolean;
}

interface HyperElementsOptions {
  publishableKey: string;
  clientSecret: string;
  loader?: 'auto' | 'always' | 'never';
  appearance?: {
    theme: 'default' | 'midnight' | 'brutal' | 'charcoal' | 'soft' | 'bubblegum' | 'none';
    variables?: Record<string, string>;
  };
}

// Error boundary for Hyperswitch SDK
class HyperswitchErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; errorMessage: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Hyperswitch payment form failed to load:', error, errorInfo);
    Sentry.captureException(error, {
      tags: { 'payment.component': 'HyperswitchErrorBoundary', 'payment.page': 'InvoicePayment' },
      extra: { componentStack: errorInfo.componentStack }
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="border border-red-300 bg-red-50 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 text-red-500 mr-3" />
            <h3 className="text-lg font-medium text-red-800">Payment System Unavailable</h3>
          </div>
          <p className="text-red-700 mb-4">
            We're having trouble loading the payment form. This could be due to a network issue or temporary service interruption.
          </p>
          <div className="space-y-2 text-sm text-red-600">
            <p><strong>What you can do:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Refresh this page and try again</li>
              <li>Check your internet connection</li>
              <li>Contact support for assistance</li>
            </ul>
          </div>
          <div className="mt-4 pt-4 border-t border-red-200">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium min-h-[44px] transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main payment component
const InvoicePaymentInner: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  const [invoiceData, setInvoiceData] = useState<InvoicePaymentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<HyperswitchError | null>(null);
  const [alreadyPaid, setAlreadyPaid] = useState(false);

  // Get formatted error display
  const errorDisplay = paymentError ? formatErrorForDisplay(paymentError) : null;

  // Check for payment error from redirect URL params
  useEffect(() => {
    const redirectStatus = searchParams.get('redirect_status');
    if (redirectStatus && redirectStatus !== 'succeeded') {
      const parsedError = parseRedirectError(searchParams);
      if (parsedError) {
        logPaymentError('Invoice payment redirect', parsedError, { token, redirectStatus });
        Sentry.captureMessage('Invoice payment failed via redirect', {
          level: 'warning',
          tags: {
            'payment.redirectStatus': redirectStatus,
            'payment.errorCode': parsedError.code,
            'payment.page': 'InvoicePayment'
          },
          extra: { token, parsedError }
        });
        setPaymentError(parsedError);
      }
      // Clean up URL params
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams, token]);

  // Load invoice data
  useEffect(() => {
    const loadInvoiceData = async () => {
      if (!token) {
        setError('Payment link is invalid');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/invoice-payment/${token}`);
        const data = await response.json();

        if (!response.ok || !data.success) {
          if (data.alreadyPaid) {
            setAlreadyPaid(true);
          }
          throw new Error(data.error || 'Failed to load invoice information');
        }

        setInvoiceData(data.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load invoice information');
      } finally {
        setLoading(false);
      }
    };

    loadInvoiceData();
  }, [token]);

  // Hyperswitch checkout form component
  const HyperswitchCheckoutForm: React.FC<{
    invoiceData: InvoicePaymentData;
    token: string;
  }> = ({ invoiceData, token }) => {
    // Configure UnifiedCheckout with saved payment methods
    // Key difference from patient payment: displaySavedPaymentMethods: true
    const unifiedCheckoutOptions = {
      layout: { type: 'tabs' as const },
      business: { name: invoiceData.companyName || invoiceData.clinicName },
      // Note: 'loader' option belongs in HyperElements options, not here
      // Set card as default payment method to avoid SDK warning
      paymentMethodOrder: ['card', 'google_pay', 'apple_pay'],
      fields: {
        billingDetails: {
          name: 'auto' as const,
          email: 'auto' as const,
          phone: 'auto' as const,
          address: 'auto' as const
        }
      },
      wallets: {
        walletReturnUrl: `${window.location.origin}/invoice-payment/success/${token}`,
        applePay: 'auto' as const,
        googlePay: 'auto' as const
      },
      sdkHandleConfirmPayment: {
        handleConfirm: true,
        buttonText: `Pay $${invoiceData.amountDue.toFixed(2)}`,
        confirmParams: {
          return_url: `${window.location.origin}/invoice-payment/success/${token}`,
        },
      },
      // Show saved payment methods - physician can select from their saved cards
      displaySavedPaymentMethods: true,
      // Don't offer to save new cards from this flow
      displaySavedPaymentMethodsCheckbox: false
    };

    return (
      <div className="space-y-4">
        <style>{`
          /* Hide Hyperswitch branding */
          [class*="powered"],
          [class*="hyperswitch"],
          [class*="Powered"],
          [data-testid*="powered"],
          [class*="footer"],
          [class*="Footer"],
          a[href*="hyperswitch"] {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            opacity: 0 !important;
          }

          [id*="unified-checkout"] > div:last-child:empty,
          [id*="unified-checkout"] [class*="footer"]:empty {
            display: none !important;
          }
        `}</style>
        <UnifiedCheckout
          id="unified-checkout"
          options={unifiedCheckoutOptions}
          onReady={() => {
            console.log('Payment form loaded successfully');
            setTimeout(() => {
              const brandingElements = document.querySelectorAll('[class*="powered"], [class*="hyperswitch"], [class*="Powered"]');
              brandingElements.forEach(el => (el as HTMLElement).style.display = 'none');
            }, 1000);
          }}
          onError={(error: any) => {
            const parsedError = parseSDKError(error);
            logPaymentError('Invoice UnifiedCheckout', parsedError, {
              token,
              invoiceNumber: invoiceData.invoiceNumber
            });
            Sentry.captureException(error, {
              tags: {
                'payment.component': 'UnifiedCheckout',
                'payment.errorCode': parsedError.code,
                'payment.page': 'InvoicePayment'
              },
              extra: {
                token,
                invoiceNumber: invoiceData.invoiceNumber,
                parsedError
              }
            });
            setPaymentError(parsedError);
          }}
        />
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading invoice information...</p>
        </div>
      </div>
    );
  }

  // Already paid state
  if (alreadyPaid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Invoice Already Paid</h1>
            <p className="text-gray-600">This invoice has already been paid. Thank you!</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !invoiceData) {
    const isExpiredError = error?.includes('expired');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            {isExpiredError ? (
              <Clock className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            ) : (
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            )}
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              {isExpiredError ? 'Payment Link Expired' : 'Payment Link Error'}
            </h1>
            <p className="text-gray-600 mb-4">{error}</p>
            {isExpiredError && (
              <p className="text-sm text-gray-500">
                Please contact the clinic to request a new payment link.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Check if invoice is already paid
  if (invoiceData.status === 'paid' || invoiceData.amountDue <= 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">Invoice Paid</h1>
            <p className="text-gray-600">
              Invoice {invoiceData.invoiceNumber} has been paid in full.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main payment page
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Invoice Payment</h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">
                Pay invoice {invoiceData.invoiceNumber}
              </p>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500 self-start sm:self-auto">
              <Shield className="h-4 w-4 flex-shrink-0" />
              <span>Secure Payment</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Invoice Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Summary</h2>

            {/* Invoice and Company Info */}
            <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Invoice Number</p>
                  <p className="font-medium text-gray-900">{invoiceData.invoiceNumber}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Building className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">From</p>
                  <p className="font-medium text-gray-900">{invoiceData.companyName}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Due Date</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(invoiceData.dueDate, { month: 'long' })}
                  </p>
                </div>
              </div>
            </div>

            {/* Amount Breakdown */}
            <div className="space-y-3 mb-6">
              {/* Every component gets a line, and the breakdown shows whenever
                  any of them is non-zero — not just when shipping is.
                  Previously a telemed visit fee was added to the total but
                  never listed, so subtotal + shipping came up short of
                  "Invoice Total" with no explanation for the difference. */}
              {((invoiceData.shippingAmount ?? 0) > 0
                || (invoiceData.telemedNetworkFee ?? 0) > 0
                || (invoiceData.taxAmount ?? 0) > 0) ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="text-gray-900">${(invoiceData.subtotal ?? 0).toFixed(2)}</span>
                  </div>
                  {(invoiceData.shippingAmount ?? 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Shipping</span>
                      <span className="text-gray-900">${(invoiceData.shippingAmount ?? 0).toFixed(2)}</span>
                    </div>
                  )}
                  {(invoiceData.telemedNetworkFee ?? 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Telemed visit fee</span>
                      <span className="text-gray-900">${(invoiceData.telemedNetworkFee ?? 0).toFixed(2)}</span>
                    </div>
                  )}
                  {(invoiceData.taxAmount ?? 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax</span>
                      <span className="text-gray-900">${(invoiceData.taxAmount ?? 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                    <span className="text-gray-600 font-medium">Invoice Total</span>
                    <span className="text-gray-900 font-medium">${invoiceData.totalAmount.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Invoice Total</span>
                  <span className="text-gray-900">${invoiceData.totalAmount.toFixed(2)}</span>
                </div>
              )}
              {invoiceData.amountPaid > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Amount Paid</span>
                  <span className="text-green-600">-${invoiceData.amountPaid.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Amount Due */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">Amount Due</span>
                <span className="text-2xl font-bold text-blue-600">
                  ${invoiceData.amountDue.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Saved Cards Notice */}
            {invoiceData.hasHyperswitchCustomer && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <CreditCard className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Your saved cards will be displayed
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      Select a saved card and enter the CVV to confirm payment.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Payment Form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h2>

            {paymentError && errorDisplay && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-red-800">{errorDisplay.title}</p>
                    <p className="text-red-700 mt-1">{errorDisplay.description}</p>
                    {errorDisplay.suggestion && (
                      <p className="text-sm text-red-600 mt-2">{errorDisplay.suggestion}</p>
                    )}
                    {errorDisplay.errorCode && (
                      <p className="text-xs text-red-500 mt-2 font-mono">
                        Error code: {errorDisplay.errorCode}
                      </p>
                    )}
                    {errorDisplay.canRetry && (
                      <button
                        onClick={() => setPaymentError(null)}
                        className="mt-3 text-sm text-red-700 hover:text-red-800 underline"
                      >
                        Dismiss and try again
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <HyperswitchErrorBoundary>
              <HyperswitchCheckoutForm
                invoiceData={invoiceData}
                token={token!}
              />
            </HyperswitchErrorBoundary>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component with Hyperswitch integration
export const InvoicePayment: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hyperPromiseRef = useRef<Promise<any> | null>(null);
  // One-shot guard: the backend holds an invoice-level charge claim per intent
  // (MD-2696), so a duplicate create-intent call (StrictMode double-effect)
  // would 409 and clobber a perfectly good payment session.
  const intentRequestedRef = useRef(false);
  const tenantConfig = getCurrentTenantConfig();

  // Load Hyperswitch SDK with correct environment endpoint
  useEffect(() => {
    if (!hyperPromiseRef.current) {
      const hyperswitchEnv = tenantConfig.payments.hyperswitch.environment;
      const customBackendUrl = hyperswitchEnv === 'sandbox'
        ? 'https://sandbox.hyperswitch.io'
        : 'https://api.hyperswitch.io';

      hyperPromiseRef.current = loadHyper(tenantConfig.payments.hyperswitch.publishableKey, {
        customBackendUrl
      });
    }
  }, [tenantConfig.payments.hyperswitch.publishableKey]);

  // Fetch client secret for invoice payment
  useEffect(() => {
    const fetchClientSecret = async () => {
      if (!token) {
        setError('Invalid payment link');
        setLoading(false);
        return;
      }
      if (intentRequestedRef.current) return;
      intentRequestedRef.current = true;

      try {
        const response = await fetch(`/api/invoice-payment/${token}/create-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });

        const data = await response.json();

        if (response.status === 409) {
          // Expected transient charge-claim conflict (MD-2696): a charge is already
          // in flight for this invoice (the claim self-clears within 3 min). Show the
          // server's friendly message and return WITHOUT logging — console.error is
          // auto-captured as a Sentry issue, and this benign state isn't worth one.
          setError(data.error || 'A payment for this invoice is already in progress. Please wait a moment and try again.');
          return;
        }

        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to initialize payment');
        }

        if (data.data?.clientSecret) {
          setClientSecret(data.data.clientSecret);
        } else {
          throw new Error('Failed to create payment session');
        }
      } catch (err) {
        console.error('Payment initialization failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize payment');
      } finally {
        setLoading(false);
      }
    };

    fetchClientSecret();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Initializing secure payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isExpiredError = error.includes('expired');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            {isExpiredError ? (
              <Clock className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            ) : (
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            )}
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              {isExpiredError ? 'Payment Link Expired' : 'Payment Error'}
            </h1>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!clientSecret || !hyperPromiseRef.current) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Preparing payment form...</p>
        </div>
      </div>
    );
  }

  const hyperswitchOptions: HyperElementsOptions = {
    publishableKey: tenantConfig.payments.hyperswitch.publishableKey,
    clientSecret,
    loader: 'auto',  // Display skeleton UI while loading
    appearance: {
      theme: 'default',
      variables: {
        colorPrimary: '#3B82F6',
        colorBackground: '#ffffff',
        colorText: '#374151',
        borderRadius: '8px'
      }
    }
  };

  return (
    <HyperElements options={hyperswitchOptions} hyper={hyperPromiseRef.current}>
      <InvoicePaymentInner />
    </HyperElements>
  );
};
